import React from "react";

import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import { push } from "connected-react-router";

import moment from "moment";

import * as DatasetAPI from "api/DatasetAPI";
import * as ProjectAPI from "api/ProjectAPI";

import { CHART_TYPES } from "containers/Report/ReportReducer";

import * as nextReducer from "containers/Next/NextReducer";
import * as homeReducer from "containers/Home/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as marketReducer from "containers/Market/MarketReducer";
import * as tasksReducer from "containers/Tasks/TasksReducer";

import { AibleClientFactory } from "client/aible/factory";
const PROJECT_CLIENT = AibleClientFactory.getInstance().getProjectClient();

export const readableSearch = {
  EXPLORE_CHART: "Explore Chart",
  OPTIMIZE_TRANSACTION: "Update Baseline",
  OPTIMIZE_STRATEGY: "Update Strategy",
  OPTIMIZE_COSTS: "Update Costs",
  OPTIMIZE_BALANCER: "Balance Costs From Project",
  OPTIMIZE_COSTS_BASELINE: "Update Baseline Costs",
  OPTIMIZE_BLUEPRINT: "Add Business Use Case",
  DEPLOY_REVIEW: "Deploy Scenario",
  FAIR_GUIDE: "Update Fairness",
  FAIR_VALUE: "Update Subgroup",
  BUDGET_SUBGROUP: "Update Subgroup",
  DEPLOY_INTEGRATION: "Integrate Deployed AI",
  DEPLOY_MONITOR: "Deployed Monitoring Settings",
  PREDICT_WHAT_IF: "Try Test Predictions",
  EVAL_MODEL_DRIVERS: "Evaluate Model Drivers",
  REVIEW_MODEL_METRICS: "Review Model Metrics",
  REVIEW_MODEL_CHARTS: "Review Model Charts",
  OUTCOME: "Dataset Settings"
};

const UPDATE_BASE = "nextModels/UPDATE_BASE";
const UPDATE_FIELD = "nextModels/UPDATE_FIELD";
const UPDATE_FIELD_VALUE = "nextModels/UPDATE_FIELD_VALUE";
const UPDATE_HEX = "nextModels/UPDATE_HEX";
const UPDATE_HOVER = "nextModels/UPDATE_HOVER";

const initialNext = {};

function modelsReducer(state = initialNext, action) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.nextModels;
}

export function getSelectedOverall(state) {
  return getBase(state).selectedOverall;
}

export function getSetupActions(state) {
  return getBase(state).setupActions ? getBase(state).setupActions : [];
}

export function getModels(state, id) {
  return getBase(state)[id] ? getBase(state)[id] : {};
}

export function getIsLoading(state, id) {
  return getModels(state, id).isLoading;
}

export function getSelectedHex(state, id) {
  return getModels(state, id).selectedHex;
}

export function getPinnedHex(state, id) {
  return getModels(state, id).pinnedHex;
}

export function getActionHexMap(state, id) {
  return getModels(state, id).actionHexMap;
}

export function getHexMap(state, id) {
  return getModels(state, id).hexMap;
}

export function getSavedHexMap(state, id) {
  return getModels(state, id).savedHexMap;
}

export function getHexSize(state, id) {
  return getModels(state, id).hexSize;
}

export function getInnerHexSize(state, id) {
  return getModels(state, id).innerHexSize;
}

export function getHexagonRadius(state, id) {
  return getModels(state, id).hexagonRadius;
}

export function getHoverGroup(state, id) {
  return getModels(state, id).hoverGroup;
}

export function getHoverRibbon(state, id) {
  return getModels(state, id).hoverRibbon;
}

export function getHoverNode(state, id) {
  return getModels(state, id).hoverNode;
}

export function getSearchTimeline(state, id) {
  return getModels(state, id).searchTimeline || [];
}

export function getBeforeReplayHexMap(state, id) {
  return getModels(state, id).beforeReplayHexMap;
}

export function getSetupSection(state, id) {
  return getModels(state, id).setupSection;
}

export function getNodeAssign(state, id) {
  return getModels(state, id).nodeAsssign || {};
}

export function getComments(state, id) {
  return getModels(state, id).comments || {};
}

export function getViewTask(state, id) {
  return getModels(state, id).viewTask;
}

export function getSavedActions(state, id) {
  return getModels(state, id).savedActions !== undefined ? getModels(state, id).savedActions : [];
}

export function getReplayIndex(state, id) {
  return getModels(state, id).replayIndex !== undefined ? getModels(state, id).replayIndex : -1;
}

export function getIsReplay(state, id) {
  return getModels(state, id).isReplay;
}

export function getIsTodo(state, id) {
  return getModels(state, id).isTodo;
}

export function getIsFixUpdate(state, id) {
  return getModels(state, id).isFixUpdate;
}

export function getIsCommentModal(state, id) {
  return getModels(state, id).isCommentModal;
}

export function getTodoMaximize(state, id) {
  return getModels(state, id).todoMaximize || {};
}

export function getBeforeSetup(state, id) {
  return getModels(state, id).beforeSetup || {};
}

export function getIsEditBlueprint(state, id) {
  return getModels(state, id).isEditBlueprint;
}

export function getTodos(state, id, includeTemporary = false) {
  let todos = getModels(state, id).todos || {};

  if (includeTemporary === true) {
    const temporaryTodo = getTemporaryTodo(state, id);

    if (temporaryTodo?.id !== undefined && todos[temporaryTodo?.id] === undefined) {
      return { ...todos, [temporaryTodo?.id]: temporaryTodo };
    }
  }

  return todos;
}

export function getTemporaryTodo(state, id) {
  return getModels(state, id).temporaryTodo;
}

export function getIsSelectedTemporary(state, id) {
  const selectedNode = getSelectedHex(state, id);
  const todos = getTodos(state, id);

  return todos?.[selectedNode?.id]?.isTemporary;
}

export function getHasConsultantAction(state, id) {
  const actions = getSavedActions(state, id);

  const fAction = actions.find((a) => a.isConsultantAction === true);

  return fAction === undefined ? false : true;
}

export function getIsDeployOpen(state, id) {
  return getModels(state, id).isDeployOpen;
}

export function getHasActions(state, id) {
  const actionHexMap = getActionHexMap(state, id);

  return actionHexMap !== undefined && Object.keys(actionHexMap).length > 0;
}

export function getCenterHex(state, id) {
  return getModels(state, id).centerHex ? getModels(state, id).centerHex : { x: 8, y: 8 };
}

export function getSelectedNode(state, id) {
  const selectedHex = getSelectedHex(state, id);
  const actionHexMap = getActionHexMap(state, id);
  const savedHexMap = getSavedHexMap(state, id);

  let selectedKey = undefined;

  if (selectedHex) {
    selectedKey =
      Object.keys(actionHexMap).find((aKey) => actionHexMap[aKey]?.id && actionHexMap[aKey]?.id === selectedHex?.id) ||
      Object.keys(savedHexMap).find((sKey) => savedHexMap[sKey]?.id && savedHexMap[sKey]?.id === selectedHex?.id);
  }

  let selectedNode = selectedKey === undefined ? undefined : actionHexMap[selectedKey] || savedHexMap[selectedKey];

  if (savedHexMap[selectedKey]) {
    selectedNode = { ...selectedNode, parent: undefined };
  }

  return selectedNode;
}

export function getSelectedSection(state, id) {
  const selectedNode = getSelectedNode(state, id);

  return selectedNode?.leftSection;
}

export function getAllHex(state, id) {
  return {
    ...getHexMap(state, id),
    ...getSavedHexMap(state, id),
    ...getActionHexMap(state, id)
  };
}

export function getSuppressAutoAddReviewstate(state, id) {
  return getModels(state, id).suppressAutoAddReview;
}

export function getNeighborFromPosition(state, id, x, y, sx = undefined, sy = undefined) {
  const hexagonRadius = getHexagonRadius(state, id);

  const startX = sx !== undefined ? sx : hexagonRadius;
  const startY = sy !== undefined ? sy : hexagonRadius;

  let yDelta = -1;

  if (startX % 2 !== 0) {
    yDelta = 1;
  }

  const fieldIndex =
    x === startX && y === startY - 1
      ? 0
      : x === startX + 1 && y === startY + yDelta
        ? 1
        : x === startX + 1 && y === startY
          ? 2
          : x === startX && y === startY + 1
            ? 3
            : x === startX - 1 && y === startY
              ? 4
              : x === startX - 1 && y === startY + yDelta
                ? 5
                : undefined;

  return fieldIndex;
}

export function getFieldFromPosition(state, id, x, y, sx = undefined, sy = undefined) {
  const datasetId = nextReducer.getDatasetId(state, id);

  const rankedFields = homeReducer.getRankedFields(state, datasetId);
  const neighborIndex = getNeighborFromPosition(state, id, x, y, sx, sy);

  return neighborIndex !== undefined && rankedFields !== undefined ? rankedFields[neighborIndex] : undefined;
}

export function getSelectedNeightbors(state, id, x, y) {
  const selectedHex = getSelectedHex(state, id);

  const hexagonRadius = getHexagonRadius(state, id);

  return selectedHex === undefined ? undefined : getFieldFromPosition(state, id, x, y, hexagonRadius, hexagonRadius);
}

export function getDoubleFieldFromPosition(state, id, x, y) {
  const hexagonRadius = getHexagonRadius(state, id);
  const currentField = getFieldFromPosition(state, id, x, y);

  return currentField
    ? undefined
    : x === hexagonRadius + 1 && y === hexagonRadius - 2
      ? [getFieldFromPosition(state, id, x - 1, y + 1), getFieldFromPosition(state, id, x, y + 1)]
      : x === hexagonRadius + 2 && y === hexagonRadius
        ? [getFieldFromPosition(state, id, x - 1, y - 1), getFieldFromPosition(state, id, x - 1, y)]
        : x === hexagonRadius + 2 && y === hexagonRadius
          ? [getFieldFromPosition(state, id, x - 1, y - 1), getFieldFromPosition(state, id, x - 1, y)]
          : x === hexagonRadius + 1 && y === hexagonRadius + 1
            ? [getFieldFromPosition(state, id, x, y - 1), getFieldFromPosition(state, id, x - 1, y)]
            : x === hexagonRadius - 1 && y === hexagonRadius + 1
              ? [getFieldFromPosition(state, id, x + 1, y), getFieldFromPosition(state, id, x, y - 1)]
              : x === hexagonRadius - 2 && y === hexagonRadius
                ? [getFieldFromPosition(state, id, x + 1, y), getFieldFromPosition(state, id, x + 1, y - 1)]
                : x === hexagonRadius - 1 && y === hexagonRadius - 2
                  ? [getFieldFromPosition(state, id, x, y + 1), getFieldFromPosition(state, id, x + 1, y + 1)]
                  : undefined;
}

export function getHexagonFields(state, id, hex) {
  const firstOrderField = getFieldFromPosition(state, id, hex.x, hex.y);
  const doubleOrderFields = undefined; //getDoubleFieldFromPosition(state, id, hex.x, hex.y);
  const selectedNeighbors = getSelectedNeightbors(state, id, hex.x, hex.y);

  const hexMap = getHexMap(state, id);
  const hKey = hex.x + "_" + hex.y;

  return hexMap[hKey]
    ? hexMap[hKey].fields
    : firstOrderField
      ? { 0: firstOrderField }
      : doubleOrderFields
        ? { 0: doubleOrderFields[0], 1: doubleOrderFields[1] }
        : selectedNeighbors
          ? { 0: selectedNeighbors }
          : {};
}

export function getChartsFromDirection(state, id, fields, direction) {
  const charts = [];

  const viewIndex = getViewIndex(state, id);

  for (let i = viewIndex; i < 30; i++) {
    if (direction === HEXAGON_NODES.FIRST_VARIABLE && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (direction === HEXAGON_NODES.SECOND_VARIABLE && Object.keys(fields).length > 1) {
      charts.push(getSecondOrder(state, id, fields[1], i));
    } else if (direction === HEXAGON_NODES.DRILL_IN && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (direction === HEXAGON_NODES.DRILL_IN_CLUSTER && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (Object.keys(fields).length > 0) {
      charts.push(getRandomSecondOrder(state, id, fields[0], i));
    }
  }

  return charts;
}

export function findNodeInSaved(state, id, node) {
  const savedHexMap = getSavedHexMap(state, id) ?? {};

  const fKey = Object.keys(savedHexMap).find(
    (h) => savedHexMap[h]?.id !== undefined && savedHexMap[h]?.id === node?.id
  );

  return fKey === undefined ? undefined : savedHexMap[fKey];
}

export function findNodeInMap(state, id, node) {
  const savedHexMap = getSavedHexMap(state, id);
  const hexMap = getHexMap(state, id);

  const sKey = Object.keys(savedHexMap).find(
    (h) => savedHexMap[h]?.id !== undefined && savedHexMap[h]?.id === node?.id
  );

  const hKey = Object.keys(hexMap).find((h) => hexMap[h]?.id !== undefined && hexMap[h]?.id === node?.id);

  return sKey !== undefined ? savedHexMap[sKey] : hKey !== undefined ? hexMap[hKey] : undefined;
}

export function getNeighborLocation(x, y) {
  let secondItemY = 0;
  let thirdItemY = 1;
  let lastItemY = 0;

  if (x % 2 === 0) {
    secondItemY = -1;
    thirdItemY = 0;
    lastItemY = -1;
  }

  return {
    [0]: {
      x: x,
      y: y - 1
    },
    [1]: {
      x: x + 1,
      y: y + secondItemY
    },
    [2]: {
      x: x + 1,
      y: y + thirdItemY
    },
    [3]: {
      x: x,
      y: y + 1
    },
    [4]: {
      x: x - 1,
      y: y + thirdItemY
    },
    [5]: {
      x: x - 1,
      y: y + lastItemY
    }
  };
}

export function getDistanceFromNode(aX, aY, bX, bY) {
  return Math.abs(aX - bX) + Math.abs(aY - bY);
}

export function getNodeFromHex(state, id, hex) {
  const hexMap = getHexMap(state, id);
  const actionHexMap = getActionHexMap(state, id);

  return hexMap[hex.x + "_" + hex.y] || actionHexMap[hex.x + "_" + hex.y];
}

export function getSavedNodeFromHex(state, id, hex) {
  const savedHexMap = getSavedHexMap(state, id);

  return savedHexMap[hex.x + "_" + hex.y];
}

export function getDoubleNeighborLocation(state, id, x, y) {
  const hexMap = getHexMap(state, id);
  const savedHexMap = getSavedHexMap(state, id);

  const neighbors = getNeighborLocation(x, y);

  let retNeighbors = [];

  for (const nKey of Object.keys(neighbors)) {
    const neighbor = neighbors[nKey];

    const nextNeighbors = getNeighborLocation(neighbor.x, neighbor.y);

    for (const iKey of Object.keys(nextNeighbors)) {
      const nextNeighbor = nextNeighbors[iKey];

      if (
        hexMap &&
        hexMap[nextNeighbor.x + "_" + nextNeighbor.y] === undefined &&
        savedHexMap[nextNeighbor.x + "_" + nextNeighbor.y] === undefined &&
        retNeighbors.find((n) => n.x === nextNeighbor.x && n.y === nextNeighbor.y) === undefined
      ) {
        retNeighbors.push(nextNeighbor);
      }
    }
  }

  return retNeighbors.sort((a, b) => {
    if (getNeighborFromPosition(state, id, a.x, a.y, x, y) !== undefined) {
      return -1;
    } else if (getNeighborFromPosition(state, id, b.x, b.y, x, y) !== undefined) {
      return 1;
    }

    return getDistanceFromNode(a.x, a.y, x, y) > getDistanceFromNode(b.x, b.y, x, y)
      ? 1
      : getDistanceFromNode(a.x, a.y, x, y) < getDistanceFromNode(b.x, b.y, x, y)
        ? -1
        : 0;
  });
}

export function getTransformedField(state, id, field) {
  const datasetId = nextReducer.getDatasetId(state, id);

  const dataset = homeReducer.getDataset(state, datasetId);
  const rankedFields = homeReducer.getRankedFields(state, datasetId);

  const fIndex = rankedFields.findIndex((f) => f.label === field);

  if (fIndex === -1) {
    return undefined;
  }

  return {
    ...dataset.fields[field],
    rank: fIndex,
    color: colors[fIndex % colors.length] ?? "gray"
  };
}

export function getHasBeforeReplayHexMap(state, id) {
  const hexMap = getBeforeReplayHexMap(state, id);

  return hexMap && Object.keys(hexMap).length > 0 ? true : false;
}

export function getBlueprintText(state, id) {
  const projectId = nextReducer.getProjectId(state, id);
  const runId = projectReducer.getRunId(state, projectId);

  const blueprintId = setupReducer.getBlueprintId(state, runId);

  const blueprint = blueprintId === undefined ? undefined : blueprintReducer.getBlueprint(state, blueprintId);

  return blueprint?.name;

  /*
  return blueprint !== undefined
    ? blueprint.name +
        (blueprint?.wizard?.dataset?.name !== undefined
          ? " based off of Dataset: " + blueprint?.wizard?.dataset?.name
          : "")
    : undefined;
  */
}

export function getIsProjectLoading(state, id) {
  const projectId = nextReducer.getProjectId(state, id);

  const optimized = projectReducer.getOptimized(state, projectId);

  return optimized?.isCreating;
}

export function updateBase(base) {
  return {
    type: UPDATE_BASE,
    data: base
  };
}

export function updateSelectedOverall(selectedOverall) {
  return { type: UPDATE_FIELD, key: "selectedOverall", data: selectedOverall };
}

export function updateSetupActions(setupAction) {
  return { type: UPDATE_FIELD, key: "setupAction", data: setupAction };
}

export function updateHexMap(id, hexMap) {
  return { type: UPDATE_FIELD_VALUE, id: id, key: "hexMap", data: hexMap };
}

export function updateSavedActions(id, savedActions) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: savedActions,
    key: "savedActions"
  };
}

export function updateReplayIndex(id, replayIndex) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: replayIndex,
    key: "replayIndex"
  };
}

export function updateIsReplay(id, isReplay) {
  return (dispatch, getState) => {
    if (isReplay === false) {
      if (window.replayTimeouts) {
        window.replayTimeouts.forEach((timeout) => clearTimeout(timeout));
      }
    }

    dispatch(updateSetupSection(id, undefined));

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isReplay,
      key: "isReplay"
    });
  };
}

export function updateSavedHexMap(id, savedHexMap) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "savedHexMap",
    data: savedHexMap
  };
}

export function updateIsLoading(id, isLoading) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "isLoading",
    data: isLoading
  };
}

export function updateActionHexMap(id, actionHexMap) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "actionHexMap",
    data: actionHexMap
  };
}

export function updateCenterHex(id, centerHex) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "centerHex",
    data: centerHex
  };
}

export function updateHoverNode(id, hoverNode) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "hoverNode",
    data: hoverNode
  };
}

export function updateIsDeployOpen(id, isDeployOpen) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "isDeployOpen",
    data: isDeployOpen
  };
}

export function updateBeforeReplayHexMap(id, beforeReplayHexMap) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: beforeReplayHexMap,
    key: "beforeReplayHexMap"
  };
}

export function updateSetupSection(id, setupSection) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: setupSection,
    key: "setupSection"
  };
}

export function updateIsFixUpdate(id, isFixUpdate) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: isFixUpdate,
    key: "isFixUpdate"
  };
}

export function deleteTodo(id, todo) {
  return (dispatch, getState) => {
    let todos = { ...getTodos(getState(), id) };

    if (todo?.mNode?.id !== undefined) {
      delete todos[todo?.mNode?.id];

      dispatch(updateTodos(id, todos));
    }
  };
}

export function assignTodo(id, todo) {
  return (dispatch, getState) => {
    dispatch(marketReducer.assignTodo(id, todo));
  };
}

export function editNodeAssign(id, nodeId, todoEdits, tIndex, isDelete = false) {
  return (dispatch, getState) => {
    let nodeAsssign = { ...getNodeAssign(getState(), id) };

    if (nodeAsssign?.[nodeId]?.[tIndex] !== undefined) {
      if (isDelete === true) {
        nodeAsssign[nodeId].splice(tIndex, 1);
      } else {
        nodeAsssign[nodeId][tIndex] = {
          ...nodeAsssign[nodeId][tIndex],
          ...todoEdits
        };
      }
    }

    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: nodeAsssign,
      key: "nodeAsssign"
    });

    return dispatch(updateIsFixUpdate(id, !getIsFixUpdate(getState(), id)));
  };
}

export function editComment(id, nodeId, commentEdits, tIndex, isDelete = false) {
  return (dispatch, getState) => {
    let comments = { ...getComments(getState(), id) };

    if (comments?.[nodeId]?.[tIndex] !== undefined) {
      if (isDelete === true) {
        comments[nodeId].splice(tIndex, 1);
      } else {
        comments[nodeId][tIndex] = {
          ...comments[nodeId][tIndex],
          ...commentEdits
        };
      }
    }

    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: comments,
      key: "comments"
    });

    return dispatch(updateIsFixUpdate(id, !getIsFixUpdate(getState(), id)));
  };
}

export function updateIsCommentModal(id, isCommentModal) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "isCommentModal",
    data: isCommentModal
  };
}

export function updateIsEditBlueprint(id, isEditBlueprint) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "isEditBlueprint",
    data: isEditBlueprint
  };
}

export function saveUpdateBlueprint(id) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const optimized = projectReducer.getOptimized(getState(), projectId);

    const blueprintId = optimized?.setup?.blueprintId;

    if (blueprintId !== undefined) {
      const blueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

      dispatch(
        blueprintReducer.saveBlueprint(
          blueprintId,
          blueprint?.name,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
          optimized?.setup?.customValues
        )
      );
    }
  };
}

export function createComment(comment, isExplore = false) {
  return (dispatch, getState) => {
    const taskId = comment?.node?.id;
    const task = tasksReducer.getTask(getState(), taskId);

    dispatch(projectReducer.addProjectTaskComment(taskId, comment?.textBody)).then((comment) => {
      dispatch(tasksReducer.fetchComments(taskId));
    });
  };
}

export function updateComment(id, node, comment) {
  return (dispatch, getState) => {
    let comments = { ...getComments(getState(), id) };

    if (comments?.[node?.id] === undefined) {
      comments[node?.id] = [];
    }

    comments[node?.id].push({
      author: loginReducer.getUserName(getState()),
      title: comment?.node?.text,
      text: comment?.textBody,
      date: new Date(),
      id: comment?.node?.id,
      type: "modelsComment"
    });

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: comments,
      key: "comments"
    });
  };
}

export function updateViewTask(id, viewTask) {
  return (dispatch, getState) => {
    const activeTab = nextReducer.getActiveTab(getState(), id);

    if (activeTab === "EXPLORE") {
      const metadata = viewTask?.versions?.[0]?.data?.metadata;

      const chartNode = nextReducer.getNodeFromMetadata(getState(), id, metadata);

      return dispatch(nextReducer.updateSearchChart(id, chartNode, chartNode));
    }

    if (viewTask === undefined) {
      dispatch(resetBeforeSetup(id));
      dispatch(resetActions(id));
    } else {
      const projectId = nextReducer.getProjectId(getState(), id);
      const project = projectReducer.getProject(getState(), projectId);

      const activeSegment = projectReducer.getActiveSegment(getState(), id);

      const taskSegmentId = viewTask?.projects?.[0]?.segments?.[0]?.id;

      if (activeSegment?.id !== taskSegmentId) {
        const taskSegment = project?.segments ? project.segments.find((s) => s.id === taskSegmentId) : undefined;
        const taskRunId = taskSegment?.job?.job_id;
        const taskDrilldown = taskSegment?.conditions?.[0]?.value || "Overall";

        dispatch(projectReducer.updateDrilldownValue(projectId, taskRunId, taskDrilldown));
      }

      const setupObj = viewTask?.versions?.[0]?.data;
      if (setupObj !== undefined) {
        const oldViewTask = getViewTask(getState(), id);
        if (oldViewTask === undefined) {
          dispatch(setBeforeSetup(id));
        }

        const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

        const runId = activeSegment?.job?.job_id;

        const retSetup = {
          ...setupReducer.getSetup(getState(), runId),
          ...setupObj
        };

        dispatch(setupReducer.updateSetup(runId, retSetup));
        dispatch(projectReducer.resetBudget(projectId));
      }
    }

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: viewTask,
      key: "viewTask"
    });
  };
}

export function rejectTask(id, task) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

    return dispatch(projectReducer.rejectTask(task?.id));
  };
}

export function likeTask(id, task) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);

    PROJECT_CLIENT.addProjectTaskRating(task?.id, { feedback: "LIKE" }).then((response) => {
      dispatch(projectReducer.fetchProject(projectId));
    });
  };
}

export function dislikeTask(id, task) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);

    PROJECT_CLIENT.addProjectTaskRating(task?.id, { feedback: "DISLIKE" }).then((response) => {
      dispatch(projectReducer.fetchProject(projectId));
    });
  };
}

export function approveTask(id, task) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const activeSegment = projectReducer.getActiveSegment(getState(), projectId);
    const project = projectReducer.getProject(getState(), projectId);

    const allScenarios = projectReducer.getAllScenarios(getState(), projectId);
    if (!allScenarios || allScenarios.length < 1) {
      dispatch(projectReducer.saveScenario(projectId, "Default Scenario"));
    } else {
      dispatch(setBeforeSetup(id));

      const currentScenario = projectReducer.getCurrentScenario(getState(), projectId);

      const oScenario = currentScenario?.original;

      const scenarioSegments = project.segments
        .filter((s) => s.permissions.can_draft)
        .map((s) => {
          let rSetup = setupReducer.getSetup(getState(), s.job.job_id);
          const frontier = projectReducer.getCurrentFrontier(getState(), id, s.id);

          let setup = {
            ...JSON.parse(JSON.stringify(rSetup)),
            frontier: frontier
          };

          setup.defaultSetup = { data: rSetup };

          const assumptions = [];

          //test
          if (
            s.assumption === undefined ||
            rSetup.fronter === undefined ||
            !projectReducer.isSameSetup(s.assumption.data, setup, false)
          ) {
            assumptions.push({
              type: "ALL",
              data: setup
            });
          }

          return {
            id: s.id,
            assumptions: assumptions
          };
        })
        .filter((x) => x.assumptions.length > 0);

      const retScenario = {
        name: oScenario?.name,
        status: "DRAFT",
        segments: scenarioSegments
      };

      if (oScenario) {
        ProjectAPI.patchScenario(oScenario?.id, retScenario).then((response) => {
          toast(
            <div>{"The task: " + (readableSearch[task?.type] || task?.type) + " has been successfully applied!"}</div>,
            {
              type: toast.TYPE.SUCCESS,
              position: toast.POSITION.TOP_CENTER,
              autoClose: 7500
            }
          );
          dispatch(projectReducer.fetchProject(projectId));
        });
      }
    }

    const hexMap = getHexMap(getState(), id);

    let foundParentNode = undefined;
    Object.keys(hexMap).forEach((hKey) => {
      const pNode = hexMap[hKey];

      pNode?.selected &&
        Object.keys(pNode.selected).forEach((index) => {
          const cNode = pNode.selected[index];

          if (cNode.id === task?.type) {
            foundParentNode = pNode;
          }
        });
    });

    if (foundParentNode) {
      dispatch(updateSelectedHex(id, foundParentNode, foundParentNode));

      const actionHexMap = getActionHexMap(getState(), id);

      let foundChildNode = undefined;
      Object.keys(actionHexMap).forEach((hKey) => {
        const cNode = actionHexMap[hKey];
        if (cNode.id === task?.type) {
          foundChildNode = cNode;
        }
      });

      if (foundChildNode) {
        dispatch(updateSelectedHex(id, foundChildNode, foundChildNode));
        dispatch(handleUpdate(id, foundChildNode, foundChildNode));

        const savedHexMap = getSavedHexMap(getState(), id);

        const foundSaveKey = Object.keys(savedHexMap).find((sKey) => savedHexMap[sKey]?.id === task?.type);

        if (foundSaveKey !== undefined) {
          setTimeout(() => centerHexByHkey(foundSaveKey, 250), 200);
        }
      }
    }

    dispatch(updateViewTask(id, undefined));
    dispatch(projectReducer.approveTask(task?.id));

    return dispatch(nextReducer.saveNext(id, undefined, false));
  };
}

export function deleteTask(id, task) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

    return dispatch(projectReducer.removeTaskFromSegment(activeSegment?.id, task?.id));
  };
}

export function createSegmentTaskFromTodo(id, segment, todo) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);

    const setupData = buildSetupStep(getState(), id, todo?.node);
    const activeSetupData = setupData[segment?.job?.job_id];

    dispatch(
      projectReducer.createSegmentTask(
        segment.id,
        todo?.assigneeId,
        todo?.node?.id,
        todo?.assignee + " - " + (readableSearch[todo?.node?.id] || todo?.node?.id),
        undefined,
        todo?.textBody
      )
    ).then((response) => {
      dispatch(tasksReducer.fetchTasks());
    });

    //export function addUserRoleToSegment(projectId, segmentId, userId, role) {

    /*
    dispatch(
      projectReducer.addUserRoleToSegment(
        projectId,
        segment?.id,
        todo?.assigneeId,
        "VIEWER"
      )
    );
    */
  };
}

// This is the task assign function
export function updateNodeAssign(id, node, todo) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

    const isOverallSegment = projectReducer.getIsOverallSegment(getState(), projectId, activeSegment?.job?.job_id);

    if (isOverallSegment === true) {
      const project = projectReducer.getProject(getState(), projectId);

      project?.segments &&
        project.segments.forEach((segment) => dispatch(createSegmentTaskFromTodo(id, segment, todo)));
    } else {
      return dispatch(createSegmentTaskFromTodo(id, activeSegment, todo));
    }
  };
}

export function updateSavedNode(id, node, update) {
  return (dispatch, getState) => {
    let savedHexMap = { ...getSavedHexMap(getState(), id) };

    const fKey = Object.keys(savedHexMap).find((k) => savedHexMap[k]?.id === update?.id);

    if (fKey !== undefined) {
      savedHexMap[fKey] = {
        ...update
      };

      dispatch(updateSavedHexMap(id, savedHexMap));
    }

    return Promise.resolve();
  };
}

export function updateTodos(id, todos) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: todos,
    key: "todos"
  };
}

export function updateSuppressAutoAddReview(id, bool) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: bool,
    key: "suppressAutoAddReview"
  };
}

export function updateTemporaryTodo(id, temporaryTodo) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: temporaryTodo,
    key: "temporaryTodo"
  };
}

export function updatePinnedHex(id, pinnedHex) {
  return (dispatch, getState) => {
    if (pinnedHex === undefined) {
      const oldPinnedHex = getPinnedHex(getState(), id);

      dispatch(filterSearchTimeline(id, oldPinnedHex));
    }

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "pinnedHex",
      data: pinnedHex
    });
  };
}

export function updateBeforeSetup(id, beforeSetup) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "beforeSetup",
    data: beforeSetup
  };
}

export function resetHex(id) {
  return (dispatch, getState) => {
    const actions = getSavedActions(getState(), id);
    const vActions = actions.filter((a) => a.isVisible !== false);

    dispatch(updateIsReplay(id, false));
    dispatch(updateReplayIndex(id, vActions.length));
    //dispatch(resetActions(id, undefined, false));

    const beforeReplayHexMap = getBeforeReplayHexMap(getState(), id);

    if (beforeReplayHexMap) {
      dispatch(updateHexMap(id, beforeReplayHexMap.hex));
      dispatch(updateSavedHexMap(id, beforeReplayHexMap.saved));
    }

    return dispatch(updateBeforeReplayHexMap(id, undefined));
  };
}

export function resetHexMap(id) {
  return (dispatch, getState) => {
    dispatch(updateHexMap(id, {}));
    dispatch(updateHexMap(id, getInitialHexMap(getState(), id, { x: 8, y: 8 })));

    /*
    const hexagonRadius = 8;
    let hexMap = { ...initTransform.savedHexMap };
    let actionHexMap = {};

    const neighbors = getNeighborLocation(hexagonRadius, hexagonRadius);

    const node = hexMap[hexagonRadius + "_" + hexagonRadius];

    const topActions = getTopActions(getState(), id);

    let nIndex = 0;
    for (let nKey of Object.keys(neighbors)) {
      const neighbor = neighbors[nKey];

      if (node && node.selected) {
        if (node.selected[nIndex]) {
          hexMap[neighbor.x + "_" + neighbor.y] = {
            ...node.selected[nIndex],
            parent: hex,
            color: node.color,
          };
        }
      } else if (topActions[nIndex]) {
        hexMap[neighbor.x + "_" + neighbor.y] = {
          ...topActions[nIndex],
        };
      }

      nIndex++;
    }

    console.log("first hes map before repl");
    console.log(hexMap);

    dispatch(updateHexMap(id, hexMap));
    return dispatch(updateActionHexMap(id, actionHexMap));

    */
  };
}

export function getIsInSaved(state, id, node) {
  const savedHexMap = getSavedHexMap(state, id);

  let found = false;

  Object.keys(savedHexMap ?? {}).forEach((k) => {
    if (node?.leftSection !== undefined && savedHexMap[k]?.leftSection === node?.leftSection) {
      found = true;
    }

    if (node?.sectionType !== undefined && savedHexMap[k]?.sectionType === node?.sectionType) {
      found = true;
    }
  });

  return found;
}

export function findNextNeighbor(state, id, x, y, hMap = undefined) {
  const hexMap = hMap ? hMap : getHexMap(state, id);
  const savedHexMap = getSavedHexMap(state, id);

  const neighbors = getNeighborLocation(x, y);
  const nextNeighbor = Object.keys(neighbors).find((nKey) => {
    const nHex = neighbors[nKey];
    return savedHexMap[nHex.x + "_" + nHex.y] === undefined;
  });

  return neighbors[nextNeighbor];
}

export function getInitialHexMap(state, id, cHex = undefined) {
  let hexMap = { ...initTransform.hexMap };
  const savedHexMap = getSavedHexMap(state, id);

  //const centerHex = cHex ? cHex : getCenterHex(state, id);

  const centerHex = { x: 8, y: 8 };

  const neighbors = getNeighborLocation(centerHex.x, centerHex.y);

  const node = hexMap[centerHex.x + "_" + centerHex.y];

  const topActions = getTopActions(state, id);

  let nIndex = 0;
  for (let nKey of Object.keys(neighbors)) {
    const neighbor = neighbors[nKey];

    if (getIsInSaved(state, id, topActions[nIndex]) !== true) {
      if (savedHexMap[neighbor.x + "_" + neighbor.y] === undefined) {
        if (node && node.selected) {
          if (node.selected[nIndex]) {
            hexMap[neighbor.x + "_" + neighbor.y] = {
              ...node.selected[nIndex],
              parent: hex,
              color: node.color,
              x: neighbor.x,
              y: neighbor.y
            };
          }
        } else if (topActions[nIndex]) {
          hexMap[neighbor.x + "_" + neighbor.y] = {
            ...topActions[nIndex],
            x: neighbor.x,
            y: neighbor.y
          };

          nIndex++;
        }
      }
    } else {
      nIndex++;
    }
  }

  return hexMap;
}

export function addSetupAction(setupAction) {
  return (dispatch, getState) => {
    const setupActions = [...getSetupActions(getState()), setupAction];

    return dispatch(updateSetupActions(setupActions));
  };
}

export function addSavedAction(id, action) {
  return (dispatch, getState) => {
    const isReplay = getIsReplay(getState(), id);

    if (isReplay !== true) {
      const replayIndex = getReplayIndex(getState(), id);

      let savedActions = [...getSavedActions(getState(), id)];
      const vActions = savedActions.filter((a) => a.isVisible !== false);

      if (replayIndex !== vActions.length - 1 && false) {
        //dispatch(resetHex(id));
      } else if (action.isVisible !== false) {
        dispatch(updateReplayIndex(id, replayIndex + 1));
      }

      savedActions.push(action);
      //savedActions.push({ ...action, isConsultantAction: true });
      dispatch(updateSavedActions(id, savedActions));
    }

    return Promise.resolve();
  };
}

export function resetBeforeSetup(id, nBeforeSetup = undefined) {
  return (dispatch, getState) => {
    const beforeSetup = nBeforeSetup !== undefined ? nBeforeSetup : getBeforeSetup(getState(), id);

    if (beforeSetup) {
      Object.keys(beforeSetup).forEach((runId) => {
        const rSetup = beforeSetup[runId];

        dispatch(setupReducer.updateSetup(runId, { ...rSetup }));
      });

      const projectId = nextReducer.getProjectId(getState(), id);
      dispatch(projectReducer.resetBudget(projectId));
    }

    return Promise.resolve();
  };
}

export function filterSearchTimeline(id, node) {
  return (dispatch, getState) => {
    const searchTimeline = getSearchTimeline(getState(), id).filter((s) => s?.value !== node?.id);

    if (searchTimeline.length !== getSearchTimeline(getState(), id).length) {
      dispatch(updateSearchTimeline(id, searchTimeline));
    }

    return Promise.resolve();
  };
}

export function handleBack(id, stopReset = false) {
  return (dispatch, getState) => {
    dispatch(
      addSavedAction(id, {
        type: "HANDLE_BACK",
        isVisible: false
      })
    );

    const selectedNode = getSelectedNode(getState(), id);

    dispatch(filterSearchTimeline(id, selectedNode));

    //dispatch(updateIsTodo(id, false));

    if (selectedNode?.hasUpdate && stopReset !== true) {
      dispatch(resetBeforeSetup(id));
    }

    if (selectedNode?.parent) {
      return dispatch(updateSelectedHex(id, selectedNode?.parent, selectedNode?.parent, true));
    } else {
      dispatch(resetActions(id));
    }
  };
}

export function resetActions(id) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "selectedHex",
      data: undefined
    });

    dispatch(updateActionHexMap(id, {}));

    return dispatch(updateHexMap(id, getInitialHexMap(getState(), id, { x: 8, y: 8 })));
  };
}

export function viewAndStartReplay(id, user) {
  return (dispatch, getState) => {
    const datasetId = nextReducer.getDatasetId(getState(), id);

    dispatch(push("/next/" + datasetId + "/" + id));

    dispatch(updateIsLoading(id, true));
    dispatch(nextReducer.updateActiveTab(id, "MODELS"));

    return setTimeout(() => {
      dispatch(updateIsLoading(id, false));
      dispatch(replayActions(id, user));
    }, 5000);
  };
}

export function addChildNode(id, parentNode, childNode) {
  return (dispatch, getState) => {
    let savedHexMap = getSavedHexMap(getState(), id);
    let saveHex = nextHexDelta(parentNode, parentNode?.childDelta, parentNode?.oChildDelta);

    // Try to find an empty spot a maximum of 10 times
    let foundSave = false;

    if (findNodeInSaved(getState(), id, childNode) === undefined) {
      const hexMap = getHexMap(getState(), id);

      for (let i = 0; i < 10; i++) {
        if (
          savedHexMap[saveHex.x + "_" + saveHex.y] === undefined &&
          hexMap[saveHex.x + "_" + saveHex.y] === undefined
        ) {
          savedHexMap[saveHex.x + "_" + saveHex.y] = {
            ...saveHex,
            ...childNode,
            index: Object.keys(savedHexMap).length,
            updatedDate: Date.now(),
            author: loginReducer.getUserName(getState())
          };
          foundSave = true;
          break;
        } else {
          saveHex = nextHexDelta(saveHex, parentNode?.childDelta, parentNode?.oChildDelta);
        }
      }
    }

    return dispatch(updateSavedHexMap(id, savedHexMap));
  };
}

export function removeNode(id, node) {
  return (dispatch, getState) => {
    let savedHexMap = { ...getSavedHexMap(getState(), id) };
    const hexMap = getHexMap(getState(), id);

    const sKey = Object.keys(savedHexMap).find((s) => savedHexMap[s]?.id === node?.id);

    if (sKey !== undefined) {
      savedHexMap[sKey] = undefined;

      const cKeys = Object.keys(savedHexMap).filter(
        (s) => savedHexMap[s]?.parent?.id !== undefined && savedHexMap[s]?.parent?.id === node?.parent?.id
      );

      let retNodes = [];
      cKeys.forEach((cKey) => {
        retNodes.push(savedHexMap[cKey]);
        savedHexMap[cKey] = undefined;
      });

      retNodes.forEach((rNode) => {
        let saveHex = nextHexDelta(rNode?.parent, rNode?.parent?.childDelta, rNode?.parent?.oChildDelta);

        for (let i = 0; i < 10; i++) {
          if (
            savedHexMap[saveHex.x + "_" + saveHex.y] === undefined &&
            hexMap[saveHex.x + "_" + saveHex.y] === undefined
          ) {
            savedHexMap[saveHex.x + "_" + saveHex.y] = {
              ...saveHex,
              ...rNode
            };
            break;
          } else {
            saveHex = nextHexDelta(saveHex, rNode?.parent?.childDelta, rNode?.parent?.oChildDelta);
          }
        }
      });

      dispatch(updateSavedHexMap(id, savedHexMap));
    }

    return Promise.resolve();
  };
}

export function handleStartBreadcrumbs(id, node, isAll, isRemove) {
  return (dispatch, getState) => {
    const oTodos = getTodos(getState(), id);
    let todos = { ...getTodos(getState(), id) };

    const neighbors = getNeighborLocation(node?.x, node?.y);

    const hexMap = getHexMap(getState(), id);
    let savedHexMap = { ...getSavedHexMap(getState(), id) };

    let nodesToAdd =
      node?.selected === undefined
        ? []
        : Object.keys(node?.selected).filter((nodeIndex) => node.selected[nodeIndex].afterSetup === undefined);

    let foundTodos = false;
    let shouldReview = false;
    if (node?.selected && isAll === true) {
      const actionHexMap = getActionHexMap(getState(), id);
      nodesToAdd.forEach((s, i) => {
        const nKey = Object.keys(actionHexMap).find((aKey) => actionHexMap[aKey]?.id === node?.selected[s]?.id);
        const nNode = actionHexMap[nKey];

        if (nNode?.afterSetup === undefined) {
          if (nNode?.hasUpdate) {
            shouldReview = true;
          }

          if (isRemove !== true) {
            todos[nNode.id] = {
              ...nNode,
              ...neighbors[i],
              isTemporary: false,
              index: Object.keys(oTodos).length + i
            };

            dispatch(addChildNode(id, node, nNode));
          } else {
            dispatch(removeNode(id, nNode));
            todos[nNode.id] = undefined;
            foundTodos = true;
          }
        }
      });
    } else {
      if (todos[node?.id] === undefined) {
        todos[node?.id] = {
          ...node,
          isTemporary: false,
          index: Object.keys(oTodos).length
        };

        if (node?.hasUpdate) {
          shouldReview = true;
        }

        dispatch(addChildNode(id, node?.parent, node));
      } else {
        dispatch(removeNode(id, node));
        todos[node?.id] = undefined;
      }
    }
    //check if user previously ignored review model
    if (getSuppressAutoAddReviewstate(getState(), id)) shouldReview = false;
    // Auto add review todo if they added an update todo
    if (shouldReview === true) {
      const hexMap = getHexMap(getState(), id);

      const pKey = Object.keys(hexMap).find((hKey) => hexMap[hKey]?.id === "PREDICT");

      if (pKey) {
        const predictNode = hexMap[pKey];

        const rKey = Object.keys(predictNode?.selected).find(
          (s) => predictNode?.selected[s]?.id === "EVAL_MODEL_DRIVERS"
        );

        if (rKey) {
          const reviewNode = {
            ...predictNode?.selected[rKey],
            isTemporary: false,
            parent: predictNode,
            color: "#7E69D7",
            actionId: 999
          };

          dispatch(addChildNode(id, predictNode, reviewNode));

          if (todos[reviewNode?.id] === undefined) {
            todos[reviewNode?.id] = {
              ...reviewNode,
              isTemporary: false,
              index: Object.keys(todos).length
            };
          }
        }
      }
    }

    const hasTodos = Object.keys(todos).filter((t) => todos[t] !== undefined).length > 0;

    dispatch(updateIsTodo(id, hasTodos === true ? true : false));

    /*
    if (foundTodos !== true) {
      let didFind = false;
      nodesToAdd.forEach((s) => {
        if (didFind === false) {
          const sNode = node?.selected[s];

          let isFound = false;

          savedActions.forEach((action) => {
            if (
              action?.node?.id !== undefined &&
              action?.node?.id === sNode?.id
            ) {
              isFound = true;
            }
          });

          if (isFound !== true) {
            const actionHexMap = getActionHexMap(getState(), id);

            const fKey = Object.keys(actionHexMap).find(
              (aKey) => actionHexMap[aKey].id === sNode.id
            );

            if (fKey !== undefined) {
              const fNode = actionHexMap[fKey];

              dispatch(updateSelectedHex(id, fNode, fNode));
            }

            didFind = true;
          }
        }
      });
    }
    */

    return dispatch(updateTodos(id, todos));
  };
}

export function replayActions(id, user) {
  return (dispatch, getState) => {
    if (window.replayTimeouts) {
      window.replayTimeouts.forEach((timeout) => clearTimeout(timeout));
    }
    window.replayTimeouts = [];

    dispatch(updateIsReplay(id, true));

    dispatch(updateReplayIndex(id, -1));

    let replayIndex = getReplayIndex(getState(), id);

    const savedActions = getSavedActions(getState(), id);

    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectReducer.getRunId(getState(), projectId);

    const firstSetupAction = savedActions.find((a) => a.beforeSetup && Object.keys(a.beforeSetup).length > 0);

    if (firstSetupAction) {
      dispatch(resetBeforeSetup(id, firstSetupAction.beforeSetup));
      /*
      dispatch(setupReducer.updateSetup(runId, firstSetupAction.beforeSetup));
      dispatch(projectReducer.resetBudget(projectId));
      */
    }

    const vActions = savedActions.filter(
      (a) =>
        (user === "Aible User" && a.isConsultantAction !== true) ||
        (user !== "Aible User" && a.isConsultantAction === true)
    );

    if (replayIndex === -1 || replayIndex === vActions.length || replayIndex === vActions.length - 1) {
      if (replayIndex === vActions.length || replayIndex === vActions.length - 1) {
        dispatch(updateReplayIndex(id, -1));
      }

      const savedHexMap = getSavedHexMap(getState(), id);
      dispatch(
        updateBeforeReplayHexMap(id, {
          saved: savedHexMap,
          hex: getHexMap(getState(), id)
        })
      );
      dispatch(updateSavedHexMap(id, { ...initTransform.savedHexMap }));

      dispatch(resetHexMap(id));

      nextReducer.centerHex({ x: 8, y: 8 }, true);
    }

    replayIndex = getReplayIndex(getState(), id);

    let originalReplayIndex = replayIndex;

    const rActions = vActions.filter((a, i) => i > replayIndex).filter((a) => a.type !== "AIBLE");

    const retReplayIndex = replayIndex + 1;

    window.replayTimeouts.push(
      setTimeout(() => dispatch(updateIsReplay(id, false)), (vActions.length - retReplayIndex) * 4000 + 3000 + 1000)
    );

    let aIndex = 0;

    if (user !== "Aible User") {
      const cActions = savedActions.filter((a) => a.isVisible !== false && a.isConsultantAction !== true);

      cActions.forEach((c) => {
        dispatch(handleAction(id, c, user, true));
      });

      dispatch(updateReplayIndex(id, cActions.length - 1));
    }

    rActions
      .filter((a) => a.isInstant !== true)
      .forEach((action, i) => {
        let stopAction = false;

        window.replayTimeouts.push(
          setTimeout(
            () => {
              if (getIsReplay(getState(), id) === false) {
                return;
              }

              const nReplayIndex = getReplayIndex(getState(), id);

              const realIndex = action.isVisible === false ? nReplayIndex : 1 + nReplayIndex;

              // First move to hex
              setTimeout(() => {
                if (action && action.hex) {
                  centerHex(action.hex, true);
                } else if (action && action.selectionDirection && action.selectionDirection.parent) {
                  //nextReducer.centerHex(action.selectionDirection.parent, true);
                } else if (action && action.node) {
                  centerHex(action.node, true);
                }
              }, 100);

              // Then do action
              setTimeout(() => {
                if (action && action.hex) {
                  const aKey = action.hex.x + "_" + action.hex.y;

                  const pHexTile =
                    document.getElementById("hexTile_" + aKey + "_0") ||
                    document.getElementById("hexTile_" + aKey + "_a") ||
                    document.getElementById("hexTile_" + aKey + "_n") ||
                    document.getElementById("hexTile_" + aKey);

                  const hexGridInner = document.getElementById("hexGridInner");

                  if (pHexTile && hexGridInner) {
                    let d = document.createElement("div");
                    d.className = "clickEffect";
                    d.style.left = pHexTile.offsetLeft + pHexTile.offsetWidth / 2 + "px";
                    d.style.top = pHexTile.offsetTop + pHexTile.offsetHeight / 2 + "px";
                    hexGridInner.appendChild(d);

                    d.addEventListener(
                      "animationend",
                      function () {
                        d.parentElement.removeChild(d);
                      }.bind(this)
                    );
                  }
                }

                dispatch(handleAction(id, action, user));
                dispatch(updateReplayIndex(id, realIndex));

                let checkNext = true;
                while (rActions.length > aIndex && checkNext) {
                  if (rActions[aIndex].isInstant) {
                    setTimeout(() => dispatch(handleAction(id, rActions[aIndex], user)), 50);
                    aIndex++;
                  } else {
                    checkNext = false;
                  }
                }

                aIndex++;
              }, 1000);
            },
            i * 4000 + 3000
          )
        );
      });
  };
}

export function handleAction(id, action, stopAction, isSkip) {
  return (dispatch, getState) => {
    if (getIsReplay(getState(), id) === false || action === undefined) {
      return;
    }

    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectReducer.getRunId(getState(), projectId);

    if (action.type === "HANDLE_UPDATE") {
      if (isSkip !== true) {
        dispatch(updateSetupSection(id, action?.node?.leftSection));
      }

      centerHex(action?.hex);

      //dispatch(setupReducer.updateSetup(runId, action.afterSetup));
      //dispatch(projectReducer.resetBudget(projectId));

      dispatch(resetBeforeSetup(id, action?.afterSetup));
      dispatch(handleUpdate(id, action.nHex, action?.node));
    }

    if (action.type === "HANDLE_BACK") {
      dispatch(updateSetupSection(id, undefined));
      dispatch(handleBack(id));
    }

    if (action.type === "UPDATE_SELECTED_HEX") {
      centerHex(action?.hex);

      dispatch(resetBeforeSetup(id, action.beforeSetup));
      /*
      dispatch(setupReducer.updateSetup(runId, action.beforeSetup));
      dispatch(projectReducer.resetBudget(projectId));
      */

      dispatch(updateSelectedHex(id, action.hex, action.n));
    }

    const aNode = action?.node;
    const sNode = stopAction?.node;

    if (
      aNode &&
      aNode.chart &&
      aNode.chart.title &&
      stopAction &&
      sNode &&
      sNode.chart &&
      aNode.chart.title === sNode.chart.title &&
      action.type === stopAction.type
    ) {
      dispatch(updateIsReplay(id, false));

      setTimeout(() => centerHex(stopAction, true), 200);
    }
  };
}

export function nextHexDelta(hex, delta, oDelta) {
  const retHex = {
    x: hex.x + (oDelta === undefined ? delta.x : hex.x % 2 !== 0 ? delta.x : oDelta.x),
    y: hex.y + (oDelta === undefined ? delta.y : hex.x % 2 !== 0 ? delta.y : oDelta.y)
  };

  return retHex;
}

export function handleUpdate(id, nHex, fNode) {
  return (dispatch, getState) => {
    const selectedHex = getSelectedHex(getState(), id);

    const hex = nHex ? nHex : selectedHex;

    let savedHexMap = { ...getSavedHexMap(getState(), id) };

    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectId === undefined ? "new" : projectReducer.getRunId(getState(), projectId);

    const node = fNode !== undefined ? fNode : getNodeFromHex(getState(), id, hex);

    const afterSetup = { ...setupReducer.getSetup(getState(), runId) };

    dispatch(
      addSavedAction(id, {
        type: "HANDLE_UPDATE",
        hex: hex,
        node: node,
        leftSection: node?.leftSection,
        mType: "MODELS",
        afterSetup: buildBeforeSetup(getState(), id),
        setupStep: buildSetupStep(getState(), id, node)
      })
    );

    if (fNode?.id === "DEPLOY_REVIEW") {
      const isReplay = getIsReplay(getState(), id);

      if (isReplay !== true) {
        dispatch(projectReducer.deploy(projectId, true));
      }
    }

    if (fNode?.id !== "FAIR_GUIDE") {
      const fairKey = Object.keys(savedHexMap).find((sKey) => savedHexMap[sKey]?.id === "FAIR_GUIDE");

      if (fairKey) {
        savedHexMap[fairKey].showWarning = true;
      }
    }

    const bSavedHexMap = { ...getSavedHexMap(getState(), id) };

    // Try to find an empty spot a maximum of 10 times
    let foundSave = false;

    if (node?.parent) {
      let saveHex = nextHexDelta(node?.parent, node?.parent?.childDelta, node?.parent?.oChildDelta);

      if (findNodeInSaved(getState(), id, node) === undefined) {
        const hexMap = getHexMap(getState(), id);
        for (let i = 0; i < 10; i++) {
          if (
            bSavedHexMap[saveHex.x + "_" + saveHex.y] === undefined &&
            hexMap[saveHex.x + "_" + saveHex.y] === undefined
          ) {
            savedHexMap[saveHex.x + "_" + saveHex.y] = {
              ...saveHex,
              ...node,
              afterSetup: afterSetup,
              setupStep: buildSetupStep(getState(), id, node),
              index: Object.keys(savedHexMap).length,
              updatedDate: Date.now(),
              author: loginReducer.getUserName(getState())
            };

            if (node?.leftSection === "SUBGROUP") {
              savedHexMap[saveHex.x + "_" + saveHex.y].afterText = "Subgroup focus: " + afterSetup?.splitColumn;
            }
            foundSave = true;
            break;
          } else {
            saveHex = nextHexDelta(saveHex, node?.parent?.childDelta, node?.parent?.oChildDelta);
          }
        }
      } else {
        let retSaveNode = {
          ...node,
          afterSetup: afterSetup,
          setupStep: buildSetupStep(getState(), id, node),
          index: Object.keys(savedHexMap).length,
          updatedDate: Date.now(),
          author: loginReducer.getUserName(getState())
        };

        if (node?.leftSection === "SUBGROUP") {
          retSaveNode.afterText = "Subgroup focus: " + afterSetup?.splitColumn;
        }

        const todos = { ...getTodos(getState(), id) };

        if (todos?.[node?.id] !== undefined) {
          todos[node?.id] = retSaveNode;
          dispatch(updateTodos(id, todos));
        }

        dispatch(updateSavedNode(id, node, retSaveNode));
      }
    }

    dispatch(updateSetupActions([]));
    // dispatch({
    //   type: UPDATE_FIELD_VALUE,
    //   id: id,
    //   key: "selectedHex",
    //   data: fNode,
    // });
    dispatch(updateActionHexMap(id, {}));
    dispatch(updateCenterHex(id, hex));

    if (foundSave) {
      dispatch(updateSavedHexMap(id, savedHexMap));
      dispatch(updateHexMap(id, getInitialHexMap(getState(), id, { x: 8, y: 8 })));
    }

    dispatch(setBeforeSetup(id));

    //dispatch(updateSelectedHex(id, node?.parent, node?.parent, true));

    const isReplay = getIsReplay(getState(), id);

    if (isReplay !== true && isEnabled("next-autosave")) {
      return dispatch(nextReducer.saveNext(id, undefined, false));
    }

    return Promise.resolve();
  };
}

export function centerHexByHkey(hKey, offset = undefined) {
  const hexTile = document.getElementById("hexTile_" + hKey);
  const leftDiv = document.getElementById("reportGalleryMiddle");
  const mainScroll = document.getElementById("mainScroll");

  hexTile &&
    hexTile.scrollIntoView({
      block: "center",
      inline: "center",
      behavior: "auto"
    });

  if (mainScroll && offset !== undefined) {
    mainScroll.scrollTo({
      left: mainScroll.scrollLeft + offset
    });
  }
}

export function centerHex(hex, isSmooth, isOffset) {
  const hKey = hex ? hex.x + "_" + hex.y : "undefined";

  const hexTile = document.getElementById("hexTile_" + hKey);
  const leftDiv = document.getElementById("reportGalleryMiddle");
  const mainScroll = document.getElementById("mainScroll");

  hexTile &&
    hexTile.scrollIntoView({
      block: "center",
      inline: "center",
      behavior: "auto"
    });

  if (isOffset === true && mainScroll) {
    mainScroll.scrollTo({
      left: mainScroll.scrollLeft - mainScroll.offsetWidth / 4
    });
  }
}

export function getHasSetupChanged(state, id) {
  const beforeSetup = getBeforeSetup(state, id);

  let hasSetupChanged = false;

  Object.keys(beforeSetup).forEach((runId) => {
    const nBeforeSetup = beforeSetup[runId];
    const currentSetup = setupReducer.getSetup(state, runId);

    if (projectReducer.isSameSetup(nBeforeSetup, currentSetup) !== true) {
      hasSetupChanged = true;
    }
  });

  return hasSetupChanged;
}

export function setBeforeSetup(id) {
  return (dispatch, getState) => {
    const beforeSetup = buildBeforeSetup(getState(), id);

    dispatch(updateBeforeSetup(id, beforeSetup));
  };
}

export function buildBeforeSetup(state, id) {
  const projectId = nextReducer.getProjectId(state, id);
  const project = projectReducer.getProject(state, projectId);

  let beforeSetup = {};

  project?.segments &&
    project?.segments.forEach((segment) => {
      const runId = segment?.job?.job_id;

      beforeSetup[runId] = { ...setupReducer.getSetup(state, runId) };
    });

  return beforeSetup;
}

export function applySetupStep(id, node, isIgnore = false) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const project = projectReducer.getProject(getState(), projectId);

    const setupType = node?.leftSection;

    if (setupType === "BLUEPRINT") {
      project?.segments &&
        project?.segments.forEach((segment) => {
          const runId = segment?.job?.job_id;

          const blueprintId = node?.setupStep?.[runId]?.["blueprintId"];

          if (blueprintId !== undefined) {
            dispatch(blueprintReducer.setBlueprint(runId, blueprintId, false, false, true));
          }
        });
    } else {
      project?.segments &&
        project?.segments.forEach((segment) => {
          const runId = segment?.job?.job_id;

          const retSetup = {
            ...setupReducer.getSetup(getState(), runId),
            ...node?.setupStep?.[runId]
          };

          dispatch(setupReducer.updateSetup(runId, retSetup));
        });
    }

    const retNode = { ...node?.node, isRevert: true };

    if (isIgnore !== true) {
      dispatch(handleUpdate(id, retNode, retNode));
    }

    return dispatch(projectReducer.resetBudget(projectId));
  };
}

export function buildSetupStep(state, id, node, forceProjectId = undefined) {
  const setupKeys = {
    BLUEPRINT: [
      "blueprintId",
      "outcomeLabel",
      "transactionLabel",
      "trueAction",
      "notPursuedTransactionLabel",
      "advancedCostLabel",
      "advancedRevenueLabel",
      "netImpactLabel",
      "falseAction",
      "unitType",
      "overPredictAction",
      "underPredictAction",
      "numberPredicted"
    ],
    STRATEGY: ["businessConstraints"],
    BASELINE: [
      "truePositive",
      "falsePositive",
      "trueNegative",
      "falseNegative",
      "pursuedRatio",
      "notPursuedRatio",
      "baselineConversionCost",
      "baselineConversionGain",
      "baselineConversionSaved",
      "baselineConversionMissed",
      "isBaselineRealityHammer",
      "timePeriod"
    ],
    EXPECTED: [
      "advancedRevenue",
      "advancedCost",
      "errorCosts",
      "customValues",
      "conversionGain",
      "conversionCost",
      "conversionSaved",
      "conversionMissed",
      "isRealityHammer",
      "baselineCounts"
    ],
    FAIR_GUIDE: ["fairnessObjective", "fairnessToleranceBound", "biasReduction", "targetPositiveAllocation"]
  };

  const projectId = forceProjectId !== undefined ? forceProjectId : nextReducer.getProjectId(state, id);
  const project = projectReducer.getProject(state, projectId);

  let setupStep = {};

  project?.segments &&
    project?.segments.forEach((segment) => {
      const runId = segment?.job?.job_id;
      const setup = { ...setupReducer.getSetup(state, runId) };

      const sKeys = setupKeys[node?.leftSection];

      setupStep[runId] = {};

      sKeys &&
        sKeys.forEach((key) => {
          setupStep[runId][key] = setup?.[key];
        });
    });

  return setupStep;
}

export function updateMarkCompleted(id, node) {
  return (dispatch, getState) => {
    let savedHexMap = { ...getSavedHexMap(getState(), id) };

    const fKey = Object.keys(savedHexMap).find((sKey) => savedHexMap[sKey]?.id === node?.id);

    if (fKey) {
      savedHexMap[fKey].isCompleted = true;

      let todos = getTodos(getState(), id);
      if (todos[node?.id] !== undefined) {
        todos[node?.id].isCompleted = true;

        dispatch(updateTodos(id, todos));
      }

      return dispatch(updateSavedHexMap(id, savedHexMap));
    }

    return Promise.resolve();
  };
}

export function checkSplits(id) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectReducer.getRunId(getState(), projectId);
    const splitColumn = setupReducer.getSplitColumn(getState(), runId);
    const hasSplits = splitColumn !== undefined;

    const savedHexMap = getSavedHexMap(getState(), id);

    if (
      hasSplits &&
      Object.keys(savedHexMap).find((sKey) => savedHexMap[sKey]?.id === "BUDGET_SUBGROUP") === undefined
    ) {
      dispatch(updateSelectedHex(id, { x: 9, y: 8 }));

      const actionHexMap = getActionHexMap(getState(), id);
      const splitNode = actionHexMap["9_7"];

      dispatch(updateSelectedHex(id, { x: 9, y: 7 }, splitNode));
      dispatch(handleUpdate(id, { x: 9, y: 7 }, splitNode));

      dispatch(handleBack(id));
    }
  };
}

export function updateSelectedHexAndSetup(id, action, actionRepeat, isInstant = undefined, blueprintId) {
  return (dispatch, getState) => {
    const node = action?.node ? action?.node : action?.mNode;

    const retNode = { ...node, showRevert: action.showRevert };

    const selectedHex = getSelectedHex(getState(), id);
    if (selectedHex?.id === undefined || selectedHex?.id !== node?.id) {
      dispatch(updateSelectedHex(id, retNode, retNode));
    }

    if (selectedHex?.id === retNode?.id && selectedHex?.showRevert !== retNode?.showRevert) {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: { ...retNode }
      });
    }

    dispatch(applySetupStep(id, action, true));
  };
}

export function updateSelectedHex(id, hex, n, isInstant = undefined, blueprintId) {
  return (dispatch, getState) => {
    if (blueprintId === "new" && n && n.leftSection && n.leftSection === "DATASET") {
      return Promise.resolve();
    }

    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectReducer.getRunId(getState(), projectId);

    dispatch(
      addSavedAction(id, {
        type: "UPDATE_SELECTED_HEX",
        hex: hex,
        n: n,
        beforeSetup: buildBeforeSetup(getState(), id),
        isVisible: false,
        isInstant: isInstant
      })
    );

    if (n?.hasUpdate) {
      setTimeout(() => centerHex(hex, undefined, true), 100);
    }

    if (n && n?.showWarning === true) {
      let retSavedHexMap = { ...getSavedHexMap(getState(), id) };

      retSavedHexMap[hex?.x + "_" + hex?.y] = { ...n, showWarning: undefined };

      dispatch(updateSavedHexMap(id, retSavedHexMap));
    }

    /*
    FIXTODO: AUTO TIMELINE SEARCH IS BROKEN

    if (n?.leftSection !== undefined) {
      const searchTimeline = [
        ...getSearchTimeline(getState(), id),
        { label: readableSearch[n?.id], value: n?.id },
      ];
      dispatch(updateSearchTimeline(id, searchTimeline));
    }
    */

    if (n && n.leftSection && n.leftSection === "CREATE") {
      return Promise.resolve();
    }

    if (n && n.leftSection && n.leftSection === "DATASET") {
      return dispatch(nextReducer.updateActiveTab(id, "TRANSFORM"));
    }

    if (n && n.leftSection && n.leftSection === "ADD_JOB") {
      dispatch(marketReducer.updateIsCreateJob(true, { id, noModal: true }));
    }

    let hexMap = { ...getHexMap(getState(), id) };

    let selectedHex = getSelectedHex(getState(), id);

    if (n?.selected) {
      dispatch(resetActions(id));

      selectedHex = getSelectedHex(getState(), id);
    }

    const todos = getTodos(getState(), id);

    if (Object.keys(todos).length === 0) {
      dispatch(updateIsTodo(id, false));
    }

    if (selectedHex && selectedHex.x === hex.x && selectedHex.y === hex.y) {
      dispatch(updateTemporaryTodo(id, undefined));

      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: undefined
      });

      dispatch(updateActionHexMap(id, {}));

      return dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
    }

    let actionHexMap = { ...getActionHexMap(getState(), id) };
    const actionNode = n?.actionId !== undefined || (actionHexMap[hex.x + "_" + hex.y] && n?.selected === undefined);

    if (actionNode) {
      dispatch(setBeforeSetup(id));

      // Open setup in the todo window now
      const todoMaximize = {
        [n?.id]: true
      };

      dispatch(updateTodoMaximize(id, todoMaximize));

      const todos = getTodos(getState(), id);

      if (todos[n?.id] === undefined) {
        dispatch(updateTemporaryTodo(id, { ...n, isTemporary: true }));
      } else {
        dispatch(updateTemporaryTodo(id, undefined));
      }

      dispatch(updateIsTodo(id, true));

      return dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: { ...hex, ...n }
      });
    }

    let node = hexMap[hex.x + "_" + hex.y];

    if (node === undefined) {
      const sNode = getSavedHexMap(getState(), id)[hex.x + "_" + hex.y];

      if (sNode && sNode.length > 0 && sNode[0].parent) {
        node = sNode[0].parent;
      }
    }

    const sNode = getSavedHexMap(getState(), id)[hex.x + "_" + hex.y];

    if (sNode?.hasUpdate && node === undefined) {
      const projectId = nextReducer.getProjectId(getState(), id);
      const runId = projectReducer.getRunId(getState(), projectId);

      const beforeSetup = buildBeforeSetup(getState(), id);

      let rHexMap = { ...getSavedHexMap(getState(), id) };

      rHexMap[hex.x + "_" + hex.y] = {
        ...sNode,
        beforeSetup: beforeSetup
      };

      dispatch(updateBeforeSetup(id, beforeSetup));

      dispatch(updateSavedHexMap(id, rHexMap));
    }

    if (node?.hasUpdate) {
      let rHexMap = { ...getHexMap(getState(), id) };

      const sNode = rHexMap[hex.x + "_" + hex.y];

      if (sNode && sNode.hasUpdate) {
        const projectId = nextReducer.getProjectId(getState(), id);
        const runId = projectReducer.getRunId(getState(), projectId);

        const beforeSetup = buildBeforeSetup(getState(), id);

        rHexMap[hex.x + "_" + hex.y] = {
          ...sNode,
          beforeSetup: beforeSetup
        };

        dispatch(updateBeforeSetup(id, beforeSetup));
      }

      dispatch(updateHexMap(id, rHexMap));
    }

    if (node && node.isSave) {
      const dTile = document.getElementById("hexTile_" + hex.x + "_" + hex.y);
      const pTile = document.getElementById("hexTile_" + node.parent.x + "_" + node.parent.y);

      if (dTile && pTile) {
        dTile.style.left = pTile.style.left;
        dTile.style.top = pTile.style.top;
      }

      dispatch(
        updateHexMap(id, {
          ...getInitialHexMap(getState(), id),
          [hex.x + "_" + hex.y]: node
        })
      );

      return setTimeout(() => {
        let savedHexMap = getSavedHexMap(getState(), id);

        if (savedHexMap[node.parent.x + "_" + node.parent.y] === undefined) {
          savedHexMap[node.parent.x + "_" + node.parent.y] = [node];
        } else {
          savedHexMap[node.parent.x + "_" + node.parent.y].push(node);
        }

        dispatch({
          type: UPDATE_FIELD_VALUE,
          id: id,
          key: "selectedHex",
          data: { x: 3, y: 3 }
        });
        dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
        return dispatch(updateSavedHexMap(id, savedHexMap));
      }, 200);
    }

    const neighbors = getNeighborLocation(hex.x, hex.y);

    let nIndex = 0;
    const consultants = marketReducer.getConsultantsArr(getState());
    for (let nKey of Object.keys(neighbors)) {
      const neighbor = neighbors[nKey];

      if (node && node.selected) {
        if (node.selected[nIndex]) {
          const foundNode = findNodeInSaved(getState(), id, node.selected[nIndex]);
          const childNode = foundNode !== undefined ? foundNode : node.selected[nIndex];

          actionHexMap[neighbor.x + "_" + neighbor.y] = {
            ...childNode,
            x: neighbor.x,
            y: neighbor.y,
            parent: { ...hex, ...node },
            color: node.color,
            actionId: nIndex
          };
        }
      } else {
        if (nIndex < 6) {
          actionHexMap[neighbor.x + "_" + neighbor.y] = {
            ...getTopActions(getState(), id)[nIndex]
          };
        }
      }

      nIndex++;
    }

    actionHexMap[hex.x + "_" + hex.y] = node;

    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "selectedHex",
      data: { ...hex, ...n }
    });

    if (n?.afterSetup === undefined && n?.leftSection !== "HOME") {
      let fNeighbor = undefined;
      let sNeighbor = undefined;

      for (let nKey of Object.keys(neighbors)) {
        const oNeighbor = neighbors[nKey];
        if (fNeighbor === undefined) {
          if (actionHexMap[oNeighbor.x + "_" + oNeighbor.y] === undefined) {
            fNeighbor = oNeighbor;
          }
        } else if (sNeighbor === undefined) {
          if (actionHexMap[oNeighbor.x + "_" + oNeighbor.y] === undefined) {
            sNeighbor = oNeighbor;
          }
        }
      }

      dispatch(updateActionHexMap(id, actionHexMap));
    }

    return Promise.resolve();
  };
}

export function getTopActionsOld(state, id) {
  let topActions = {
    0: {
      text: "Blueprint",
      afterText: "Blueprint Updated",
      selected: {},
      color: "#3A7AF5",
      leftSection: "BLUEPRINT",
      hasUpdate: true
    },
    1: {
      text: "Update Strategy",
      afterText: "Strategy Updated",
      selected: {},
      leftSection: "STRATEGY",
      color: "#7E69D7",
      hasUpdate: true
    },
    2: {
      text: "Update Expected Behavior",
      afterText: "Expected Behavior Updated",
      selected: {},
      leftSection: "EXPECTED",
      color: "#7E69D7",
      hasUpdate: true
    },
    3: {
      text: "Update Filters and Subgroup",
      afterText: "Filters and Subgroup Updated",
      selected: {},
      leftSection: "SUBGROUP",
      color: "#7E69D7",
      hasUpdate: true
    }
  };

  return topActions;
}

export function getTopActions(state, id) {
  const projectId = nextReducer.getProjectId(state, id);
  const project = projectReducer.getProject(state, projectId);
  const isContinuous =
    projectId !== undefined
      ? projectReducer.getIsContinuous(state, projectId)
      : setupReducer.getIsContinuous(state, "new");

  const runId = projectReducer.getRunId(state, projectId);

  const splitColumn = setupReducer.getSplitColumn(state, runId);

  const hasSplits = splitColumn !== undefined;

  const OPTIMIZE_SELECTED = {
    OPTIMIZE_BLUEPRINT: {
      id: "OPTIMIZE_BLUEPRINT",
      text: "Add Business Use Case Language",
      //afterText: "Use Case has been added",
      afterText: " ",
      leftSection: "BLUEPRINT",
      icon: "Blueprint",
      hasUpdate: true
    },
    OPTIMIZE_TRANSACTION: {
      id: "OPTIMIZE_TRANSACTION",
      text: "Add Baseline Business Behavior",
      afterText: "Baseline behavior has been defined",
      leftSection: "BASELINE",
      hasUpdate: true
    },
    OPTIMIZE_COSTS_BASELINE: {
      id: "OPTIMIZE_COSTS_BASELINE",
      text: "Add Baseline Business Costs",
      afterText: "Baseline business costs and benefits have been defined",
      leftSection: "EXPECTED_BASELINE",
      hasUpdate: true
    },
    OPTIMIZE_COSTS: {
      id: "OPTIMIZE_COSTS",
      text: "Add Business Costs and Benefits Calculations",
      afterText: "Business costs and benefits have been defined",
      leftSection: "EXPECTED",
      hasUpdate: true
    },
    OPTIMIZE_STRATEGY: {
      id: "OPTIMIZE_STRATEGY",
      text: "Add Business Strategy Recommendations",
      afterText: "Strategy Updated",
      leftSection: "STRATEGY",
      hasUpdate: true
    },
    OPTIMIZE_BALANCER: {
      id: "OPTIMIZE_BALANCER",
      text: "Balance Costs from another Project",
      afterText: "Project Linked",
      leftSection: "BALANCER",
      hasUpdate: true
    }
  };

  // Generate the selected hexes for balancer
  let optimizeSelected = {
    0: OPTIMIZE_SELECTED.OPTIMIZE_BLUEPRINT,
    1: OPTIMIZE_SELECTED.OPTIMIZE_TRANSACTION,
    2: OPTIMIZE_SELECTED.OPTIMIZE_COSTS
  };
  if (isContinuous) {
    optimizeSelected = {
      0: OPTIMIZE_SELECTED.OPTIMIZE_BLUEPRINT,
      1: OPTIMIZE_SELECTED.OPTIMIZE_TRANSACTION,
      2: OPTIMIZE_SELECTED.OPTIMIZE_COSTS
    };
  } else {
    optimizeSelected = {
      0: OPTIMIZE_SELECTED.OPTIMIZE_BLUEPRINT,
      1: OPTIMIZE_SELECTED.OPTIMIZE_TRANSACTION,
      2: OPTIMIZE_SELECTED.OPTIMIZE_COSTS,
      3: OPTIMIZE_SELECTED.OPTIMIZE_STRATEGY
    };

    if (isEnabled("balancer")) {
      optimizeSelected[4] = OPTIMIZE_SELECTED.OPTIMIZE_BALANCER;
    }
  }
  const OPTIMIZE = {
    id: "OPTIMIZE",
    icon: "Business",
    text: "Can this AI help my current business process?",
    selected: optimizeSelected,
    sectionType: "STRATEGY",
    color: "#7E69D7",
    childDelta: { x: 0, y: -1 },
    index: 0
  };

  const FAIRNESS = {
    id: "FAIR",
    icon: "Fairness",
    text: "I want to add fairness goals and guidelines",
    selected: {
      0: {
        id: "FAIR_VALUE",
        text: "What Subgroup Do you want to Optimize for?",
        afterText: "Subgroup focus: " + splitColumn,
        leftSection: "SUBGROUP",
        hasUpdate: true,
        borderColor: "#54C06C",
        hideSave: true
      },
      1: {
        id: "FAIR_GUIDE",
        text: "Add AI Fairness Guidelines",
        afterText: "Fairness guidelines have been defined",
        leftSection: "FAIR_GUIDE",
        hasUpdate: true,
        borderColor: "#54C06C"
      }
    },
    color: "#54C06C",
    background: "#54C06C",
    sectionType: "FAIRNESS",
    childDelta: { x: 1, y: 0 },
    oChildDelta: { x: 1, y: -1 },
    index: 1
  };

  const SUBGROUP = {
    id: "BUDGET",
    icon: "Resource",
    text: "I want Aible to find the optimal resource allocation across subgroups",
    selected: {
      0: {
        id: "BUDGET_SUBGROUP",
        text: "Select Subgroup",
        afterText: "A subgroup has been selected",
        leftSection: "SUBGROUP",
        hasUpdate: true,
        borderColor: "#00B5D0",
        hideSave: true
      }
    },
    sectionType: "BUDGET",
    background: "#00B5D0",
    color: "#00B5D0",
    childDelta: { x: 1, y: 1 },
    oChildDelta: { x: 1, y: 1 },
    index: 2
  };

  const DEPLOY = {
    id: "DEPLOY",
    icon: "Rocket",
    text: "I want to deploy the current AI",
    selected: {
      0: {
        id: "DEPLOY_REVIEW",
        text: "Review & Deploy AI",
        afterText: "AI Deployed",
        leftSection: "DEPLOY_SETTINGS",
        hasUpdate: true,
        borderColor: "#EC6417"
      },
      1: {
        id: "DEPLOY_INTEGRATION",
        text: "Integrate Deployed AI",
        afterText: "Integration has been added",
        leftSection: "INTEGRATE",
        hasUpdate: true,
        borderColor: "#EC6417"
      },
      2: {
        id: "DEPLOY_MONITOR",
        text: "Add Monitoring Settings",
        afterText: "Monitoring settings have been updated",
        leftSection: "MONITOR",
        hasUpdate: true,
        borderColor: "#EC6417"
      }
    },
    sectionType: "DEPLOY",
    color: "#EC6417",
    background: "#EC6417",
    childDelta: { x: -1, y: 1 },
    oChildDelta: { x: -1, y: 0 },
    index: 3
  };

  const PREDICT = {
    id: "PREDICT",
    icon: "Predict",
    text: "I want to see test predictions and understand model behavior",
    selected: {
      0: {
        id: "PREDICT_WHAT_IF",
        text: "Try test predictions",
        leftSection: "PREDICT_WHAT_IF",
        icon: "Stethoscope",
        borderColor: "#247ADF",
        hasUpdate: true,
        hideSave: true
      },
      1: {
        id: "EVAL_MODEL_DRIVERS",
        text: "Evaluate Model Drivers",
        leftSection: "REVIEW_AI",
        icon: "Gears",
        borderColor: "#247ADF",
        hasUpdate: true,
        hideSave: true
      },
      2: {
        id: "REVIEW_MODEL_METRICS",
        text: "Review Model Metrics",
        leftSection: "MODEL_METRICS",
        icon: "Table",
        borderColor: "#247ADF",
        hasUpdate: true,
        hideSave: true
      },
      3: {
        id: "REVIEW_MODEL_CHARTS",
        text: "Review Model Evaluation Charts",
        leftSection: "MODEL_CHARTS",
        icon: "Chart",
        borderColor: "#247ADF",
        hasUpdate: true,
        hideSave: true
      }
    },
    sectionType: "DEPLOY",
    color: "#247ADF",
    background: "#247ADF",
    childDelta: { x: 0, y: 1 },
    oChildDelta: { x: 0, y: 1 },
    index: 4
  };

  let topActions;
  // REMOVING FAIRNESS FOR CONTINUOUS FLOW
  if (isContinuous) {
    topActions = {
      0: OPTIMIZE,
      1: SUBGROUP,
      2: PREDICT,
      3: DEPLOY
    };
  } else {
    topActions = {
      0: OPTIMIZE,
      1: FAIRNESS,
      2: SUBGROUP,
      3: PREDICT,
      4: DEPLOY
    };
  }

  return topActions;
}

export function updateModels(id, models) {
  return { type: UPDATE_FIELD, id: id, data: models };
}

export function updateSearchTimeline(id, searchTimeline) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: searchTimeline,
    key: "searchTimeline"
  };
}

export function updateTodoMaximize(id, todoMaximize) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: todoMaximize,
    key: "todoMaximize"
  };
}

export function updateIsTodo(id, isTodo) {
  return (dispatch, getState) => {
    if (isTodo === false) {
      dispatch(updateTemporaryTodo(id, undefined));
    }

    return dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      data: isTodo,
      key: "isTodo"
    });
  };
}

export function saveBlueprint(id, blueprintId, isUpdateBlueprint = false, hideToast = false) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectId === undefined ? "new" : projectReducer.getRunId(getState(), projectId);

    const retBlueprintId = blueprintId ? blueprintId : setupReducer.getBlueprintId(getState(), id);

    const modelsReducer = { ...getModels(getState(), id) };

    const setup = {
      ...setupReducer.getSetup(getState(), runId),
      datasetId: nextReducer.getDatasetId(getState(), id)
    };

    const nextObj = {
      todos: getTodos(getState(), id),
      modelsReducer: modelsReducer
    };

    const label = nextReducer.getReportLabel(getState(), id);

    return dispatch(
      blueprintReducer.saveBlueprint(
        retBlueprintId === undefined ? "new" : retBlueprintId,
        label,
        setup,
        runId,
        false,
        nextObj,
        isUpdateBlueprint,
        undefined,
        hideToast
      )
    );
  };
}

export function initGrid(id, useCenterHex) {
  return (dispatch, getState) => {
    return dispatch(
      updateHexMap(id, getInitialHexMap(getState(), id, useCenterHex === true ? { x: 8, y: 8 } : undefined))
    );
  };
}

export function initAll() {
  return (dispatch, getState) => {
    const reports = reportReducer.getReports(getState());

    reports &&
      reports.forEach((report) => {
        if (report.metadata.models && Object.keys(report.metadata.models).length > 0) {
          dispatch(
            updateModels(report.id, {
              ...report.metadata.models
            })
          );
        } else {
          dispatch(init(report.id));
        }
      });

    return Promise.resolve();
  };
}

export function checkBlueprint(id, shouldFetch = true) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);

    if (projectId === undefined || projectId === "new") {
      return Promise.resolve();
    }

    const optimized = projectReducer.getOptimized(getState(), projectId);
    const project = projectReducer.getProject(getState(), projectId);

    if (optimized === undefined) {
      return setTimeout(() => dispatch(checkBlueprint(id)), 1000);
    } else {
      const blueprintId = optimized?.setup?.blueprintId;

      if (blueprintId === undefined || blueprintId === "new") {
        return Promise.resolve();
      }

      const blueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

      if (blueprint === undefined) {
        if (shouldFetch === true) {
          dispatch(blueprintReducer.fetchBlueprint(blueprintId));
        }
        return setTimeout(() => dispatch(checkBlueprint(id, false)), 1000);
      } else {
        const bCustomValues = blueprint?.wizard?.setup?.customValues;

        project &&
          project.segments &&
          project.segments.forEach((segment) => {
            const runId = segment?.job?.job_id;

            let customValues = {
              ...setupReducer.getCustomValues(getState(), runId)
            };

            let shouldUpdate = false;
            Object.keys(bCustomValues).forEach((cKey) => {
              if (customValues[cKey] === undefined) {
                customValues[cKey] = bCustomValues[cKey];
                shouldUpdate = true;
              }
            });

            if (shouldUpdate === true) {
              dispatch(setupReducer.updateCustomValues(runId, customValues));
            }
          });
      }
    }
  };
}

const initTransform = {
  hexMap: {},
  savedHexMap: {
    [8 + "_" + 8]: {
      text: "Marketing Use Case",
      selected: {},
      icon: "Blueprint",
      color: "#7e69d7",
      leftSection: "HOME",
      isBase: true,
      canStart: false,
      x: 8,
      y: 8
    },
    [7 + "_" + 7]: {
      text: "Dataset Name",
      leftSection: "DATASET",
      icon: "Join",
      isBase: true,
      color: "#059669",
      x: 7,
      y: 8
    }
  },
  actionHexMap: {}
};

export function init(id) {
  return (dispatch, getState) => {
    const retInitTransform = {
      hexMap: { ...initTransform.hexMap },
      actionHexMap: { ...initTransform.actionHexMap },
      savedHexMap: { ...initTransform.savedHexMap }
    };
    dispatch(updateModels(id, { ...retInitTransform }));

    return dispatch(initGrid(id));
  };
}

export default modelsReducer;
